import React, { Children, cloneElement } from 'react';
import ReactDOM, { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import VstListItem from './VstListItem';

const KEYCODES = {
  ENTER: 13,
  ESCAPE: 27,
  SPACE: 32,
  UP: 38,
  DOWN: 40,
};

class VstDropdown extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
    };

    this.menu = null;
    this.portal = null;
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onDocumentMouseUp = this.onDocumentMouseUp.bind(this);
    this.onTargetMouseUp = this.onTargetMouseUp.bind(this);
    this.onTargetKeydown = this.onTargetKeydown.bind(this);
    this.afterClose = this.afterClose.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.positionMenu = this.positionMenu.bind(this);
    this.onApplyClick = this.onApplyClick.bind(this);
    this.focusAtFirstChild = this.focusAtFirstChild.bind(this);
  }

  componentDidMount() {
    findDOMNode(this).addEventListener('scroll', this.closeMenu);
  }

  componentWillReceiveProps(np) {
    if (this.state.open) {
      this.renderMenu(np);
    }
  }

  componentWillUnmount() {
    if (this.state.open) {
      this.afterClose();
    }
  }

  render() {
    const target = this.props.target;
    const className = target.props.className || '';

    return cloneElement(target, {
      onClick: this.focusAtFirstChild,
      onMouseUp: this.onTargetMouseUp,
      onKeyDown: this.onTargetKeydown,
      className: `${className} ${this.state.open ? 'open' : ''}`,
      'aria-expanded': this.state.open
    });
  }

  getWinSize() {
    const body = document.body;
    const elem = document.documentElement;
    return {
      innerWidth: window.innerWidth || Math.max(elem.clientWidth, body.clientWidth),
      innerHeight: window.innerHeight || Math.max(elem.clientHeight, body.clientHeight),
      scrollY: window.scrollY || elem.scrollTop || body.scrollTop || 0,
      scrollX: window.scrollX || elem.scrollLeft || body.scrollLeft || 0,
    };
  }

  onDocumentKeydown(e) {
    if (e.keyCode === KEYCODES.ESCAPE) {
      this.closeMenu();
    }
  }

  onDocumentMouseUp(e) {
    const portal = findDOMNode(this.portal);
    if (portal && portal.contains(e.target) || (e.button && e.button !== 0)) return;
    e.stopPropagation();
    this.closeMenu();
  }

  onTargetMouseUp() {
    this.openMenu(this.props);
  }

  onTargetKeydown(e) {
    if (e.keyCode === KEYCODES.ENTER || e.keyCode === KEYCODES.SPACE || e.keyCode === KEYCODES.DOWN) {
      this.openMenu();
    }
  }

  focusMenu() {
    const portal = findDOMNode(this.portal);
    portal.firstChild.focus();
  }

  openMenu(props) {
    if (!this.state.open) {
      this.setState({ open: true }, () => {
        this.renderMenu(props || this.props);
        this.focusMenu();
        if (this.props.onOpen) {
          this.props.onOpen();
        }
      });
    }
  }

  focusAtFirstChild() {
    for (let i = 0; i < this.dropdown.children.length; i++) {
      const element = this.dropdown.children[i] && this.dropdown.children[i].firstElementChild;
      if (element && element.tagName === 'BUTTON') {
        return element.focus();
      }
    }
    return null;
  }

  afterClose() {
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('mouseup', this.onDocumentMouseUp);
    document.removeEventListener('touchstart', this.onDocumentMouseUp);
    document.removeEventListener('vst-dropdown.close', this.closeMenu);
    window.removeEventListener('resize', this.closeMenu);
    ReactDOM.unmountComponentAtNode(this.menu);
    document.body.removeChild(this.menu);
    document.body.removeChild(this.overlay);
    this.portal = null;
    this.menu = null;
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  closeMenu() {
    this.setState({ open: false }, this.afterClose);
    const targetNode = findDOMNode(this);
    targetNode.focus();
  }

  positionMenu() {
    const { horizontal, vertical, customX, customY } = this.props;
    const { innerHeight, innerWidth, scrollX, scrollY } = this.getWinSize();
    const targetNode = findDOMNode(this);
    const portalNode = findDOMNode(this.portal);
    const target = targetNode.getBoundingClientRect();
    const portal = portalNode.getBoundingClientRect();
    let menuX;
    let menuY;
    let originX;
    let originY;
    // let maxHeight;

    // calculate space above/below target
    const above = target.top;
    const below = innerHeight - target.bottom;
    // maxHeight = portal.height;
    if (horizontal === 'left') {
      // align left
      menuX = target.left + scrollX;
      originX = 'left';
      // out of bounds, move to right
      if ((menuX + portal.width) > (innerWidth + scrollX)) {
        menuX = target.right + scrollX - portal.width;
        originX = 'right';
      }
    } else {
      // align right
      menuX = target.right + scrollX - portal.width;
      originX = 'right';
      // out of bounds, move to left
      if (menuX < 0) {
        menuX = target.left + scrollX;
        originX = 'left';
      }
    }

    if (vertical === 'top') {
      // show at the top
      menuY = target.top - portal.height + scrollY;
      originY = 'bottom';
      // out of bounds, move to bottom if there is more space
      if (menuY < 0 && below > above) {
        menuY = target.bottom + scrollY;
        originY = 'top';
      }
    } else {
      // show at the bottom
      menuY = target.bottom + scrollY;
      originY = 'top';
      // out of bounds, move to top if there is more space
      if ((menuY + portal.height) > (innerHeight + scrollY) && above > below) {
        menuY = target.top - portal.height + scrollY;
        originY = 'bottom';
      }
    }

    if (originY === 'top') {
      // originY is top, which means show menu at the bottom
      if (portal.height > (innerHeight - target.bottom)) {
        // maxHeight = innerHeight - target.bottom - 10;
      }
    } else {
      // originY is top, which means show menu at the top
      if (portal.height > target.top) {
        // maxHeight = target.top - 10;
        menuY = scrollY + 10;
      }
    }

    if (customX && customY) {
      menuX = customX;
      menuY = target.top + customY;
      originY = 'right';
    }


    if (!this.props.isMobile) {
      // set initial style
      // this.menu.style.height = '0px'; // Disabling scroll on main menu for now..
      if (customX && customY) {
        this.menu.style.right = `${menuX}px`;
      } else {
        this.menu.style.left = `${menuX}px`;
      }
      this.menu.style.position = (customX && customY) ? 'absolute' : '';
      this.menu.style.top = `${menuY}px`;
      this.menu.style.transform = 'scale3d(0.01, 0.01, 1)';
      this.menu.style.transformOrigin = `${originX} ${originY}`;

      // do the animation with a slight delay
      setTimeout(() => {
        // Menu may be gone by this point if it unmounts
        if (this.menu) {
          this.menu.style.opacity = '1';
          // this.menu.style.height = `${maxHeight}px`;
          this.menu.style.transform = 'scale3d(1, 1, 1)';
          this.menu.style.transition = 'transform 0.2s ease';
        }
      }, 20);
    }
  }

  onApplyClick() {
    if (this.props.onApplyClick) {
      this.props.onApplyClick();
      this.closeMenu();
    }
  }

  renderMobileHeader() {
    return (
      <header className={this.props.headerClassName}>
        <div className="back-title">
          { this.props.showBackBtn &&
            <button onClick={this.closeMenu}>
              {this.props.headerBackButton}
            </button>
          }

          <h2 tabIndex={0}>
            {this.props.headerTitle}
          </h2>
        </div>

        { this.props.showApplyBtn &&
          <button onClick={this.onApplyClick}>
            {this.props.headerApplyButton}
          </button>
        }
      </header>
    );
  }

  renderMenu(props) {
    const { className, vertical, scrollable } = props;

    if (!this.menu) {
      this.overlay = document.createElement('div');
      this.overlay.setAttribute('class', 'vst-dropdown-overlay');
      this.menu = document.createElement('div');
      if (this.props.isMobile) {
        this.menu.className = classnames(`vst-dropdown-mobile vst-dropdown-${vertical}`, className);
      } else {
        this.menu.className = classnames(`vst-dropdown vst-dropdown-${vertical}`, className);
      }
      document.body.appendChild(this.menu);
      document.body.appendChild(this.overlay);
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('mouseup', this.onDocumentMouseUp);
      document.addEventListener('touchstart', this.onDocumentMouseUp);
      document.addEventListener('vst-dropdown.close', this.closeMenu);
      window.addEventListener('resize', this.closeMenu);
    }

    const closeMenu = this.closeMenu;
    const list = (
      <div className="vst-list">
        {this.renderMobileHeader()}
        <ul
          ref={dropdown => { this.dropdown = dropdown; }}
          style={{
            height: this.props.height,
            overflow: scrollable ? 'scroll' : 'visible',
          }}
        >
          {Children.map(props.children, (child, index) => {
            if (!child.props.hidden) {
              return cloneElement(child, {
                closeMenu,
                key: index,
                isMobile: this.props.isMobile,
                header: this.renderMobileHeader(),
              });
            }
            return null;
          })}
        </ul>
        <div aria-hidden="true" onFocus={this.focusAtFirstChild} className="focusBackToTop" tabIndex={0}></div>
      </div>
    );

    this.portal = ReactDOM.unstable_renderSubtreeIntoContainer(this, list, this.menu);
    this.positionMenu();
  }
}

VstDropdown.propTypes = {
  children: PropTypes.node.isRequired,
  target: PropTypes.element.isRequired,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  className: PropTypes.string,
  height: PropTypes.string,
  isMobile: PropTypes.bool,
  showBackBtn: PropTypes.bool,
  scrollable: PropTypes.bool,
  onApplyClick: PropTypes.func,
  showApplyBtn: PropTypes.bool,
  headerClassName: PropTypes.node,
  headerBackButton: PropTypes.node,
  headerApplyButton: PropTypes.node,
  headerTitle: PropTypes.string,
  customX: PropTypes.number,
  customY: PropTypes.number,
  horizontal: PropTypes.oneOf(['left', 'right']),
  vertical: PropTypes.oneOf(['bottom', 'top']),
};

VstDropdown.defaultProps = {
  height: null,
  horizontal: 'left',
  vertical: 'bottom',
  customX: 0,
  customY: 0,
  headerTitle: 'Title',
  scrollable: false,
  showBackBtn: true,
  showApplyBtn: true,
  menuName: '',
};

VstDropdown.Item = VstListItem;

export default VstDropdown;
